import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp, Users, FileText, DollarSign,
  Plus, Upload, BarChart2, Check, AlertTriangle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { useCompany } from '../../contexts/CompanyContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppTheme } from '../../contexts/ThemeContext';
import { EDEFTER_API_URL } from '../../config/edefterApi';
import AuthService from '../../services/authService';

const COLORS = ['#24299d', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '₺0';
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency', currency: 'TRY',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(value);
};

const cardClass =
  'bg-white dark:bg-[#1C1C1E] rounded-2xl md:rounded-[28px] p-4 md:p-6 shadow-sm border border-transparent dark:border-white/10';

/* ── react-select theme ── */
const getSelectStyles = (darkMode) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 56,
    borderRadius: 16,
    borderColor: state.isFocused ? '#24299d' : darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(36,41,157,0.2)' : 'none',
    backgroundColor: darkMode ? '#2C2C2E' : '#f9fafb',
    '&:hover': { borderColor: '#24299d' },
    cursor: state.isDisabled ? 'not-allowed' : 'default',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 16,
    backgroundColor: darkMode ? '#1C1C1E' : 'white',
    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
    boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: 8,
    backgroundColor: state.isSelected
      ? '#24299d'
      : state.isFocused
        ? darkMode ? 'rgba(255,255,255,0.08)' : '#f3f4f6'
        : 'transparent',
    color: state.isSelected ? 'white' : darkMode ? '#e5e7eb' : '#111827',
    cursor: 'pointer',
    margin: '2px 4px',
    width: 'calc(100% - 8px)',
  }),
  singleValue: (base) => ({ ...base, color: darkMode ? 'white' : '#111827' }),
  placeholder: (base) => ({ ...base, color: darkMode ? '#6b7280' : '#9ca3af' }),
  input: (base) => ({ ...base, color: darkMode ? 'white' : '#111827' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, color: darkMode ? '#6b7280' : '#9ca3af' }),
  clearIndicator: (base) => ({
    ...base, color: darkMode ? '#6b7280' : '#9ca3af',
    '&:hover': { color: '#ef4444' },
  }),
  noOptionsMessage: (base) => ({ ...base, color: darkMode ? '#9ca3af' : '#6b7280' }),
  loadingMessage: (base) => ({ ...base, color: darkMode ? '#9ca3af' : '#6b7280' }),
});

/* ── Company selector row ── */
const CompanySelector = ({ t, darkMode }) => {
  const { companies, selectedCompany, setSelectedCompany, loadingCompanies } = useCompany();
  const navigate = useNavigate();
  const notifTimerRef = useRef(null);

  const [isSelectLocked, setIsSelectLocked] = useState(!!selectedCompany);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (!selectedCompany) setIsSelectLocked(false);
  }, [selectedCompany]);

  const selectOptions = companies.map((c) => ({
    value: c.id,
    label: `${c.name}${c.vergiKimlikNo ? ` — ${c.vergiKimlikNo}` : ''}`,
    company: c,
  }));

  const selectedOption = selectedCompany
    ? selectOptions.find((o) => o.value === selectedCompany.id) ?? null
    : null;

  const handleChange = (option) => {
    const company = option ? option.company : null;
    setSelectedCompany(company);
    if (company) {
      setIsSelectLocked(true);
      if (notifTimerRef.current) clearTimeout(notifTimerRef.current);
      setNotification(company.name);
      notifTimerRef.current = setTimeout(() => setNotification(null), 3000);
    } else {
      setIsSelectLocked(false);
      setNotification(null);
    }
  };

  return (
    <>
      {notification && createPortal(
        <div className="fixed top-20 right-4 z-[100] bg-green-500 text-white px-5 py-3 rounded-2xl shadow-2xl shadow-green-500/30 text-sm font-semibold flex items-center gap-2 animate-in slide-in-from-right-4 duration-300 max-w-xs">
          <Check size={16} className="flex-shrink-0" />
          <span>{notification}</span>
        </div>,
        document.body
      )}

      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1">
          <Select
            options={selectOptions}
            value={selectedOption}
            onChange={handleChange}
            placeholder={t('nav.selectCompany')}
            noOptionsMessage={() => t('common.noData')}
            loadingMessage={() => t('nav.loadingCompanies')}
            isLoading={loadingCompanies}
            isClearable
            isDisabled={isSelectLocked}
            styles={getSelectStyles(darkMode)}
            filterOption={(opt, input) =>
              opt.label.toLowerCase().includes(input.toLowerCase())
            }
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <button
          type="button"
          onClick={() => setIsSelectLocked(false)}
          disabled={!selectedCompany}
          className="h-14 px-6 bg-gray-900 dark:bg-white text-white dark:text-black rounded-2xl font-semibold text-sm hover:opacity-90 active:scale-95 transition-all whitespace-nowrap flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100"
        >
          {t('dashboard.changeCompany')}
        </button>

        <button
          type="button"
          onClick={() => navigate('/edefter/add-company')}
          className="h-14 flex items-center gap-2 px-6 rounded-2xl shadow-lg font-semibold text-sm bg-[#24299d] text-white shadow-[#24299d]/20 hover:scale-[1.02] active:scale-95 transition-all whitespace-nowrap flex-shrink-0"
        >
          <Plus size={18} />
          {t('nav.addCompany')}
        </button>
      </div>

      {!selectedCompany && (
        <div className="flex items-start gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50 rounded-2xl p-4">
          <AlertTriangle size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
            {t('dashboard.companyRequired')}
          </p>
        </div>
      )}
    </>
  );
};

/* ── Main page ── */
const EDashboard = () => {
  const navigate = useNavigate();
  const { selectedCompany, companies, loadingCompanies, taxId } = useCompany();
  const { t } = useLanguage();
  const { darkMode } = useAppTheme();

  const currentUser = AuthService.getCurrentUser();
  const welcomeName = [currentUser?.name, currentUser?.surname].filter(Boolean).join(' ')
    || currentUser?.email || '';

  const [selectedPeriod, setSelectedPeriod] = useState('2024');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [kpiData, setKpiData] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [customerRevenue, setCustomerRevenue] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [topCustomerDetails, setTopCustomerDetails] = useState([]);

  const kpiIcons = {
    'Total Sales': DollarSign, 'Customer Count': Users,
    'Document Count': FileText, 'Average Transaction': TrendingUp,
    'Toplam Satış': DollarSign, 'Müşteri Sayısı': Users,
    'Belge Sayısı': FileText, 'Ortalama İşlem': TrendingUp,
  };
  const kpiColors = {
    'Total Sales': '#24299d', 'Customer Count': '#10b981',
    'Document Count': '#f59e0b', 'Average Transaction': '#8b5cf6',
    'Toplam Satış': '#24299d', 'Müşteri Sayısı': '#10b981',
    'Belge Sayısı': '#f59e0b', 'Ortalama İşlem': '#8b5cf6',
  };

  const fetchDashboardData = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ period: selectedPeriod, customer: selectedCustomer });
      const identifier = taxId || selectedCompany?.vergiKimlikNo || selectedCompany?.taxNumber;
      if (identifier) params.set('identifier', String(identifier).trim());

      const response = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/api/edefter/dashboard?${params}`
      );
      if (!response) throw new Error('Session expired. Please sign in again.');
      if (!response.ok) throw new Error(`Failed to load dashboard: ${response.status}`);

      const data = await response.json();
      if (data.error) throw new Error(data.error);

      const titleMap = {
        'Toplam Satış': t('dashboard.kpiTotalSales'),
        'Müşteri Sayısı': t('dashboard.kpiCustomerCount'),
        'Belge Sayısı': t('dashboard.kpiDocumentCount'),
        'Ortalama İşlem': t('dashboard.kpiAvgTransaction'),
      };
      setKpiData((data.kpiData || []).map((kpi) => ({
        ...kpi,
        title: titleMap[kpi.title] || kpi.title,
        value:
          typeof kpi.value === 'number'
            ? kpi.title === 'Toplam Satış' || kpi.title === 'Ortalama İşlem'
              ? formatCurrency(kpi.value)
              : kpi.value.toString()
            : kpi.value,
      })));
      setMonthlyRevenue(data.monthlyRevenue || []);
      setCustomerRevenue(data.customerRevenue || []);
      setDocumentTypes(data.documentTypes || []);
      setCustomers(data.customers || []);
      setTopCustomerDetails(data.topCustomerDetails || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, selectedCustomer, taxId, selectedCompany, t]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  /* Native select class — solid dark bg so text is always readable */
  const filterSelectClass =
    'h-9 px-3 rounded-xl text-sm font-medium ' +
    'bg-white dark:bg-[#2C2C2E] ' +
    'border border-gray-200 dark:border-white/10 ' +
    'text-gray-700 dark:text-white ' +
    'focus:outline-none focus:ring-2 focus:ring-[#24299d]/20 focus:border-[#24299d] ' +
    'cursor-pointer';

  return (
    <div className="space-y-6 md:space-y-8 animate-in fade-in duration-500">

      {/* ── Welcome header (always visible — like tahsis) ── */}
      <div>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
          {welcomeName ? `${t('dashboard.welcomeTitle')}, ${welcomeName} 👋` : t('dashboard.welcomeTitle')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mt-1 md:mt-2 text-sm md:text-base">
          {t('dashboard.subtitle')}
        </p>
      </div>

      {/* ── Company selector ── */}
      <CompanySelector t={t} darkMode={darkMode} />

      {/* ── No company: quick-action cards ── */}
      {!selectedCompany && !loadingCompanies && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {[
              {
                title: t('dashboard.createCompanyCard'),
                subtitle: t('dashboard.createCompanySubtitle'),
                description: t('dashboard.createCompanyDesc'),
                Icon: Plus, color: '#24299d', path: '/edefter/add-company',
              },
              {
                title: t('dashboard.uploadXmlCard'),
                subtitle: t('dashboard.uploadXmlSubtitle'),
                description: t('dashboard.uploadXmlDesc'),
                Icon: Upload, color: '#10b981', path: '/edefter/upload',
              },
            ].map(({ title, subtitle, description, Icon, color, path }) => (
              <button
                key={path}
                type="button"
                onClick={() => navigate(path)}
                className={`${cardClass} text-left hover:-translate-y-1 hover:shadow-md transition-all duration-200 group`}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: color, boxShadow: `0 8px 24px ${color}40` }}
                >
                  <Icon size={28} className="text-white" />
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">{subtitle}</p>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
                <p className="mt-3 text-sm font-semibold flex items-center gap-1" style={{ color }}>
                  {t('dashboard.getStarted')}
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </p>
              </button>
            ))}
          </div>

          {/* Feature teaser cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { Icon: TrendingUp, title: t('dashboard.realTimeAnalytics'), desc: t('dashboard.realTimeAnalyticsDesc') },
              { Icon: BarChart2, title: t('dashboard.automatedReports'), desc: t('dashboard.automatedReportsDesc') },
            ].map(({ Icon, title, desc }, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/70 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/50 dark:border-white/10">
                <div
                  className="w-12 h-12 rounded-xl bg-[#24299d] flex items-center justify-center shrink-0"
                  style={{ boxShadow: '0 4px 12px rgba(36,41,157,0.3)' }}
                >
                  <Icon size={22} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">{title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Loading ── */}
      {selectedCompany && (loading || loadingCompanies) && (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-10 h-10 border-4 border-[#24299d]/20 border-t-[#24299d] rounded-full animate-spin" />
        </div>
      )}

      {/* ── Error ── */}
      {selectedCompany && !loading && error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {/* ── Analytics (company selected + data loaded) ── */}
      {selectedCompany && !loading && !error && (
        <>
          {/* Period / Customer filters */}
          <div className={cardClass}>
            <div className="flex flex-wrap gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {t('dashboard.period')}
                </label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className={filterSelectClass}
                >
                  <option value="2024">2024</option>
                  <option value="2023">2023</option>
                  <option value="2022">2022</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {t('dashboard.customer')}
                </label>
                <select
                  value={selectedCustomer}
                  onChange={(e) => setSelectedCustomer(e.target.value)}
                  className={filterSelectClass}
                >
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {kpiData.map((kpi, idx) => {
              const Icon = kpiIcons[kpi.title] || TrendingUp;
              const color = kpiColors[kpi.title] || '#24299d';
              return (
                <div key={idx} className={cardClass}>
                  <div className="mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: color, boxShadow: `0 6px 16px ${color}30` }}
                    >
                      <Icon size={22} className="text-white" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{kpi.title}</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{kpi.value}</p>
                </div>
              );
            })}
          </div>

          {/* Charts row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <div className={`${cardClass} min-h-[380px] flex flex-col`}>
              <h3 className="text-sm md:text-base font-semibold mb-4">{t('dashboard.monthlyRevenueTrend')}</h3>
              <div className="flex-1 min-h-0">
                {monthlyRevenue.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                    <LineChart data={monthlyRevenue} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey={monthlyRevenue[0]?.monthName ? 'monthName' : 'month'} tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000000 ? `₺${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `₺${(v / 1000).toFixed(0)}K` : `₺${v}`} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }} formatter={(v) => formatCurrency(v)} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" stroke="#24299d" strokeWidth={2.5} name={t('dashboard.revenue')} dot={false} />
                      <Line type="monotone" dataKey="expense" stroke="#ef4444" strokeWidth={2.5} name={t('dashboard.expense')} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-sm text-gray-400">{t('dashboard.noDataFound')}</div>
                )}
              </div>
            </div>

            <div className={`${cardClass} min-h-[380px] flex flex-col`}>
              <h3 className="text-sm md:text-base font-semibold mb-4">{t('dashboard.customerDistribution')}</h3>
              <div className="flex-1 min-h-0">
                {customerRevenue.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                    <PieChart>
                      <Pie data={customerRevenue} cx="50%" cy="50%" labelLine={false} label={false} outerRadius="80%" innerRadius="35%" dataKey="value" paddingAngle={2}>
                        {customerRevenue.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-sm text-gray-400">{t('dashboard.noDataFound')}</div>
                )}
              </div>
            </div>
          </div>

          {/* Charts row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <div className={`${cardClass} min-h-[380px] flex flex-col`}>
              <h3 className="text-sm md:text-base font-semibold mb-4">{t('dashboard.documentTypeAnalysis')}</h3>
              <div className="flex-1 min-h-0">
                {documentTypes.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                    <BarChart data={documentTypes} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                      <XAxis dataKey="type" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000000 ? `₺${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `₺${(v / 1000).toFixed(0)}K` : `₺${v}`} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }} formatter={(v) => formatCurrency(v)} />
                      <Legend />
                      <Bar dataKey="amount" fill="#24299d" name={t('dashboard.amount')} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-sm text-gray-400">{t('dashboard.noDataFound')}</div>
                )}
              </div>
            </div>

            <div className={`${cardClass} flex flex-col`}>
              <h3 className="text-sm md:text-base font-semibold mb-4">
                {selectedCustomer === 'all' ? t('dashboard.topCustomerDetails') : t('dashboard.customerTransactionDetails')}
              </h3>
              <div className="flex-1 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-white/10">
                      <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        {selectedCustomer === 'all' ? t('dashboard.customerCol') : t('dashboard.transactionCol')}
                      </th>
                      <th className="text-right py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('dashboard.count')}</th>
                      <th className="text-right py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('dashboard.total')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-white/5">
                    {topCustomerDetails.length > 0 ? topCustomerDetails.map((c, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                        <td className="py-3 px-2 text-gray-800 dark:text-gray-200 truncate max-w-[160px]">{c.name}</td>
                        <td className="py-3 px-2 text-right text-gray-600 dark:text-gray-400">{c.count}</td>
                        <td className="py-3 px-2 text-right font-semibold text-gray-900 dark:text-white">{formatCurrency(c.value)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={3} className="py-8 text-center text-sm text-gray-400">{t('dashboard.noDataFound')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EDashboard;
